/**
 * CoSingery — **裝置身份恢復**（2026-09-23；產品負責人：「clear the history 之後所有歌唔見咗」）
 *
 * 背景：未有登入之前，「個人 library」靠 `localStorage['cosingery.deviceId']`（**弱身份**，`P-45`）
 *   → **一清瀏覽器資料（或換機），就會產生一個新 deviceId → library 即刻變空**（歌其實仍然在 DB，
 *   只係「認唔返部機」—— 實測真中：DB 有 24 首，個 app 顯示 0 首）。
 *
 * 呢個模組提供兩條**照實**嘅恢復路徑：
 *   ① URL：`https://cosingery.com/?device=<恢復碼>` → 驗證（真係有歌）→ 寫入 localStorage → 去 /chat
 *   ② UI：喺「我的」貼返**恢復碼** → 同一支驗證
 *   ⚠️ 驗證＝打 `/api/song/list?deviceId=<碼>`：**0 首就唔准寫入**（唔准扮恢復成功）
 */
import { DEVICE_STORAGE_KEY, setDeviceId } from './hooks/useChatStream';

/** 由 URL 抽恢復碼（`?device=`；亦收 `#device=` 方便貼落 address bar） */
export function deviceCodeFromUrl(search: string, hash = ''): string | null {
  const fromSearch = new URLSearchParams(search).get('device');
  const fromHash = new URLSearchParams(hash.replace(/^#/, '')).get('device');
  const code = (fromSearch ?? fromHash ?? '').trim();
  return /^[0-9a-fA-F-]{16,64}$/.test(code) ? code : null;
}

/** 形狀檢查（同 deviceId 一樣係 uuid 樣；唔會因為格式錯就去打 API） */
export function looksLikeDeviceCode(code: string): boolean {
  return /^[0-9a-fA-F-]{16,64}$/.test(code.trim());
}

/** 呢個恢復碼名下**有幾多首歌**（`null` ＝ 打唔到 API，要照實講） */
export async function countSongsForDevice(code: string): Promise<number | null> {
  try {
    const res = await fetch(`/api/song/list?deviceId=${encodeURIComponent(code.trim())}`);
    if (!res.ok) return null;
    const data = (await res.json().catch(() => ({}))) as { songs?: unknown[] };
    return Array.isArray(data.songs) ? data.songs.length : null;
  } catch {
    return null;
  }
}

/** 讀返而家用緊嘅恢復碼（＝ deviceId） */
export function currentDeviceCode(): string {
  try {
    return localStorage.getItem(DEVICE_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

/** 寫入 ＋ 記住（**只准喺驗證過之後**用） */
export function saveDeviceCode(code: string): void {
  setDeviceId(code.trim());
}

/**
 * 開場自動恢復：URL 有 `?device=` → 驗證 → 有歌就採用（並清走 URL），冇歌就**唔改**（照實）
 * 回：`'none'`（URL 冇碼）／`'adopted'`／`'empty'`（碼冇歌）／`'offline'`（驗證打唔到 API）
 */
export async function adoptDeviceFromUrl(
  search = typeof location !== 'undefined' ? location.search : '',
  hash = typeof location !== 'undefined' ? location.hash : '',
): Promise<'none' | 'adopted' | 'empty' | 'offline'> {
  const code = deviceCodeFromUrl(search, hash);
  if (!code) return 'none';
  const n = await countSongsForDevice(code);
  if (n === null) return 'offline';
  if (n === 0) return 'empty';
  saveDeviceCode(code);
  return 'adopted';
}
